import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import type { DashboardStats, ChartDataPoint, CityWiseData, Booking, DashboardStatsResponse, AnalyticsResponse } from '@/types/api.types';

export const dashboardApi = {
  getStats: async (): Promise<DashboardStats> => {
    const response = await api.get<DashboardStatsResponse>(API_ENDPOINTS.DASHBOARD.STATS);
    
    // Map API response to frontend format
    const { data } = response.data;
    return {
      totalCustomers: data.users.customers,
      totalDrivers: data.users.drivers,
      registeredDrivers: data.users.registered_drivers ?? data.users.drivers,
      totalBookings: data.bookings.total,
      totalRevenue: data.revenue.total,
      activeBookings: data.bookings.ongoing,
      pendingKYC: data.pending_kyc || 0,
      customerGrowth: data.growth?.customers ?? 0,
      driverGrowth: data.growth?.drivers ?? 0,
      bookingGrowth: data.growth?.bookings ?? 0,
      revenueGrowth: data.growth?.revenue ?? 0,
    };
  },

  getAnalytics: async (): Promise<{
    bookings: ChartDataPoint[];
    revenue: ChartDataPoint[];
  }> => {
    const response = await api.get<AnalyticsResponse>(API_ENDPOINTS.DASHBOARD.ANALYTICS);
    
    return {
      bookings: response.data.data.bookings_by_day.map((item) => ({
        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseInt(item.count, 10),
      })),
      revenue: response.data.data.revenue_by_day.map((item) => ({
        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: parseFloat(item.revenue),
      })),
    };
  },

  getCharts: async (period: string): Promise<{
    bookings: ChartDataPoint[];
    revenue: ChartDataPoint[];
  }> => {
    const response = await api.get(API_ENDPOINTS.DASHBOARD.CHARTS, { params: { period } });
    return response.data;
  },

  getRecentBookings: async (): Promise<Booking[]> => {
    const response = await api.get(API_ENDPOINTS.DASHBOARD.RECENT_BOOKINGS);
    return response.data;
  },

  getCityAnalytics: async (): Promise<CityWiseData[]> => {
    const response = await api.get(API_ENDPOINTS.DASHBOARD.CITY_ANALYTICS);
    return response.data;
  },

  getRevenueBreakdown: async (params?: {
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }): Promise<RevenueBreakdown> => {
    const response = await api.get(API_ENDPOINTS.DASHBOARD.REVENUE_BREAKDOWN, { params });
    return (response.data?.data ?? response.data) as RevenueBreakdown;
  },

  getPayoutBalance: async (): Promise<PayoutBalance> => {
    const response = await api.get(API_ENDPOINTS.DASHBOARD.PAYOUT_BALANCE);
    return (response.data?.data ?? response.data) as PayoutBalance;
  },
};

export interface PayoutBalance {
  configured: boolean;
  mode: 'production' | 'sandbox';
  available_balance: number | null;
  balance: number | null;
  error?: string;
}

export interface RevenueBreakdownOrder {
  booking_id: string;
  created_at: string;
  customer_name: string | null;
  final_fare: number;
  commission: number;
  platform_fee: number;
  shield: number;
  gst: number;
  zipto_revenue: number;
  driver_earnings: number;
}

export interface RevenueBreakdown {
  summary: {
    commission: number;
    platform_fee: number;
    shield: number;
    total: number;
    gst: number;
    driver_earnings: number;
    order_count: number;
  };
  orders: RevenueBreakdownOrder[];
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

// Mock data for development
export const mockDashboardData = {
  stats: {
    totalCustomers: 52340,
    totalDrivers: 1250,
    registeredDrivers: 1480,
    totalBookings: 156780,
    totalRevenue: 45678900,
    activeBookings: 234,
    pendingKYC: 45,
    customerGrowth: 12.5,
    driverGrowth: 8.3,
    bookingGrowth: 15.2,
    revenueGrowth: 18.7,
  } satisfies DashboardStats,

  chartData: {
    bookings: [
      { date: 'Mon', value: 1200 },
      { date: 'Tue', value: 1350 },
      { date: 'Wed', value: 1100 },
      { date: 'Thu', value: 1500 },
      { date: 'Fri', value: 1800 },
      { date: 'Sat', value: 2100 },
      { date: 'Sun', value: 1900 },
    ] satisfies ChartDataPoint[],
    revenue: [
      { date: 'Mon', value: 125000 },
      { date: 'Tue', value: 145000 },
      { date: 'Wed', value: 112000 },
      { date: 'Thu', value: 165000 },
      { date: 'Fri', value: 198000 },
      { date: 'Sat', value: 234000 },
      { date: 'Sun', value: 210000 },
    ] satisfies ChartDataPoint[],
  },

  cityWiseData: [
    { city: 'Bhubaneswar', bookings: 8500, revenue: 2150000 },
    { city: 'Cuttack', bookings: 4200, revenue: 980000 },
    { city: 'Rourkela', bookings: 2100, revenue: 520000 },
    { city: 'Brahmapur', bookings: 1800, revenue: 420000 },
  ] satisfies CityWiseData[],

  recentBookings: [
    {
      id: '1',
      bookingNumber: 'SKD-2024-0001',
      customer: { id: '1', name: 'Rajesh Kumar', phone: '9876543210' },
      driver: { id: '1', name: 'Suresh Patel', phone: '9876543211', vehicleNumber: 'OD-02-AB-1234' },
      pickup: { address: 'Sahid Nagar, Bhubaneswar', city: 'Bhubaneswar', state: 'Odisha', pincode: '751007', lat: 20.296, lng: 85.824 },
      dropoff: { address: 'Patia, Bhubaneswar', city: 'Bhubaneswar', state: 'Odisha', pincode: '751024', lat: 20.352, lng: 85.819 },
      vehicleType: 'bike',
      status: 'in_transit',
      distance: 8.5,
      duration: 25,
      fare: { baseFare: 30, distanceCharge: 85, timeCharge: 25, total: 140 },
      payment: { method: 'upi', status: 'paid' },
      createdAt: new Date().toISOString(),
    },
    {
      id: '2',
      bookingNumber: 'SKD-2024-0002',
      customer: { id: '2', name: 'Priya Sharma', phone: '9876543220' },
      driver: { id: '2', name: 'Amit Singh', phone: '9876543221', vehicleNumber: 'OD-02-CD-5678' },
      pickup: { address: 'Khandagiri, Bhubaneswar', city: 'Bhubaneswar', state: 'Odisha', pincode: '751030', lat: 20.255, lng: 85.776 },
      dropoff: { address: 'Jaydev Vihar, Bhubaneswar', city: 'Bhubaneswar', state: 'Odisha', pincode: '751013', lat: 20.298, lng: 85.806 },
      vehicleType: 'auto',
      status: 'delivered',
      distance: 12.2,
      duration: 35,
      fare: { baseFare: 50, distanceCharge: 122, timeCharge: 35, total: 207 },
      payment: { method: 'cash', status: 'paid' },
      createdAt: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: '3',
      bookingNumber: 'SKD-2024-0003',
      customer: { id: '3', name: 'Amit Patra', phone: '9876543230' },
      pickup: { address: 'Master Canteen, Bhubaneswar', city: 'Bhubaneswar', state: 'Odisha', pincode: '751001', lat: 20.270, lng: 85.841 },
      dropoff: { address: 'Nandankanan, Bhubaneswar', city: 'Bhubaneswar', state: 'Odisha', pincode: '751024', lat: 20.395, lng: 85.824 },
      vehicleType: 'mini_truck',
      status: 'pending',
      distance: 18.5,
      duration: 45,
      fare: { baseFare: 100, distanceCharge: 370, timeCharge: 90, total: 560 },
      payment: { method: 'upi', status: 'pending' },
      createdAt: new Date(Date.now() - 7200000).toISOString(),
    },
  ] as Booking[],
};
